<template>
  <div class="form">
    <div class="site">
      <custom-input
        v-model="form.name.value"
        :label="form.name.label"
        class="name-input"
        required
        type="text"
      />
        <div class="form-row">
          <picker
            v-model="form.date.value"
            class="short-input mx-2"
            :label="form.date.label"
            :with-input="true"
          />
          <picker
            v-if="!eventData.id"
            v-model="form.loop_end_date.value"
            class="short-input mx-2"
            :label="form.loop_end_date.label"
            :with-input="true"
          />
          <custom-input
            v-model="form.color.value"
            class="color-input mx-2"
            :label="form.color.label"
            required
            type="color"
          />
        </div>
        <div class="form-row">
          <custom-input
            v-model="form.start_time.value"
            class="time-input mr-2"
            :label="form.start_time.label"
            type="time"
            min="06:00"
            max="22:00"
            required
          />
          <custom-input
            v-model="form.end_time.value"
            class="time-input mx-2"
            :label="form.end_time.label"
            type="time"
            min="06:00"
            max="22:00"
            required
          />
        </div>
      <div class="d-flex w-100">
        <custom-select
          :options="trainers"
          label="full_name"
          :visible-label="form.trainer.label"
          :selected-value="form.trainer.value"
          @newSelected="setNewSelect"
        />
        <custom-checkbox
          class="w-25 mt-2"
          :checked="form.is_group.value"
          :label="form.is_group.label"
          @checkChange="changeCheck"
        />
      </div>
      <children-multiselect
        :trainer-id="form.trainer.value"
        :selected-children="downloadedSelectedChildren.list"
        @selected-children="updateChildrenList"
      />
      <custom-button
        class="button"
        button-name="Zapisz"
        @click="sendEvent"
        :disabled="buttonDisabled"
      />
      <p
        class="error-msg"
        v-if="errorMessage"
      >
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive, ref } from 'vue';
import axios from 'axios';
import dayjs from 'dayjs';

import { urlBuilder } from '../../plugins/helpers';
import messages from '../../consts/messages';
import redundantFields from '../../consts/consts';
import CustomInput from '../common/CustomInput.vue';
import Picker from '../calendar/Picker.vue';
import CustomSelect from '../common/CustomSelect.vue';
import ChildrenMultiselect from '../list/children-multiselect.vue';
import CustomButton from '../common/CustomButton.vue';
import CustomCheckbox from '../common/CustomCheckbox.vue';

export default {
  name: 'EditEventForm',
  components: {
    CustomCheckbox,
    CustomButton,
    ChildrenMultiselect,
    CustomSelect,
    CustomInput,
    Picker,
  },
  emits: ['selected-children', 'close-edit-event-modal'],
  props: {
    eventData: Object,
    times: Object,
  },
  setup(props, { emit }) {
    const form = reactive({
      name: {
        label: 'Nazwa',
        value: '',
      },
      date: {
        label: 'Data',
        value: props.eventData.date.format('DD-MM-YYYY'),
        type: 'date',
      },
      color: {
        label: 'Kolor',
        value: '#FFFFFF',
        type: 'color',
      },
      start_time: {
        label: 'Godzina rozpoczęcia',
        value: props.times.startTime,
        type: 'time',
      },
      end_time: {
        label: 'Godzina zakończenia',
        value: props.times.endTime,
        type: 'time',
      },
      trainer: {
        label: 'Trener',
        value: props.eventData.trainer,
        type: 'select',
      },
      loop_end_date: {
        label: 'Ostatnie zajęcia',
        value: null,
        type: 'date',
      },
      is_group: {
        label: 'Zajęcia grupowe',
        value: false,
      },
    });

    const trainers = ref([]);

    async function getTrainers() {
      trainers.value = (await axios.get(urlBuilder('/trainers/'))).data;
    }

    getTrainers();

    const event = reactive({ data: [] });

    async function getEventDetails() {
      event.data = (await axios.get(urlBuilder(`/events/${props.eventData.id}/`))).data;
    }

    const selectedChildren = reactive({ list: [] });

    function updateChildrenList(e) {
      selectedChildren.list = e;
    }

    const downloadedSelectedChildren = reactive({ list: [] });

    const isEdit = ref(false);

    onMounted(async () => {
      if (props.eventData.id) {
        await getEventDetails();
        isEdit.value = true;
        form.color.value = event.data.color;
        form.name.value = event.data.name;
        form.start_time.value = event.data.start_time.substr(0, 5);
        form.end_time.value = event.data.end_time.substr(0, 5);
        form.trainer.value = event.data.trainer;
        form.is_group.value = event.data.is_group;
        downloadedSelectedChildren.list = event.data.clients.map((client) => client.id);
      }
    });

    function createDateTimeObject(strTime) {
      const timeArray = strTime.split(':');
      const date = new Date();
      return date.setHours(timeArray[0], timeArray[1]);
    }

    function compareTime() {
      const startDate = createDateTimeObject(form.start_time.value);
      const endDate = createDateTimeObject(form.end_time.value);
      return startDate < endDate;
    }

    function checkTimeRange() {
      let message = '';
      const startDate = createDateTimeObject(form.start_time.value);
      const endDate = createDateTimeObject(form.end_time.value);
      const minDate = createDateTimeObject('08:00');
      const maxDate = createDateTimeObject('22:00');
      if (endDate > maxDate) {
        message = 'Zajęcia mogą się kończyć najpóźniej o 22:00';
      } else if (startDate < minDate) {
        message = 'Zajęcia mogą się rozpoczynać najwcześniej o 08:00';
      }
      return message;
    }

    const errorMessage = ref('');

    function validate() {
      let state = true;
      errorMessage.value = '';
      const emptyValues = Object.values(form)
        .filter((value) => !redundantFields.includes(value.label) && !value.value);
      if (emptyValues.length || !selectedChildren.list.length) {
        errorMessage.value = messages.required_fields_are_not_filled;
        state = false;
      } else if (!compareTime()) {
        errorMessage.value = messages.wrong_event_time;
        state = false;
      } else {
        errorMessage.value = checkTimeRange();
        if (errorMessage.value) state = false;
      }
      if (form.loop_end_date.value && dayjs(form.date.value, 'DD-MM-YYYY') >= dayjs(form.loop_end_date.value, 'DD-MM-YYYY')) {
        errorMessage.value = messages.wrong_loop_date;
        state = false;
      }
      return state;
    }

    function setMessage(error) {
      if (error === 'Events overlap') errorMessage.value = messages.events_overlap;
      else if (
        error === 'Events overlaps with the time window'
        || 'error' === 'There is no time window for events'
      ) {
        errorMessage.value = messages.events_overlaps_with_the_time_window;
      } else if (error === 'There is no time window for events') {
        errorMessage.value = messages.no_time_window_for_events;
      } else if (error === 'Event should last a minimum of 30 minutes') {
        errorMessage.value = messages.to_short_event;
      } else errorMessage.value = messages.something_wrong;
    }

    async function postCalendar() {
      if (validate()) {
        const payload = {
          date: dayjs(form.date.value, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          events: [
            {
              name: form.name.value,
              color: form.color.value,
              start_time: `${form.start_time.value}:00`,
              end_time: `${form.end_time.value}:00`,
              trainer_id: form.trainer.value,
              clients: [...selectedChildren.list],
              is_group: form.is_group.value,
              loop_end_date: (
                form.loop_end_date.value ? dayjs(form.loop_end_date.value, 'DD-MM-YYYY')
                  .format('YYYY-MM-DD') : null
              ),
            },
          ],
        };
        try {
          await axios.post(urlBuilder('/calendar/'), payload);
          emit('close-edit-event-modal');
        } catch (err) {
          setMessage(err.response.data[0]);
        }
      }
    }

    async function patchEvent() {
      if (validate()) {
        const payload = {
          day: dayjs(form.date.value, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          name: form.name.value,
          color: form.color.value,
          start_time: `${form.start_time.value}:00`,
          end_time: `${form.end_time.value}:00`,
          trainer_id: form.trainer.value,
          clients: [...selectedChildren.list],
          loop_end_date: null,
          present_clients: [],
          is_group: form.is_group.value,
        };
        try {
          await axios.patch(urlBuilder(`/events/${props.eventData.id}/`), payload);
          emit('close-edit-event-modal');
        } catch (err) {
          setMessage(err.response.data[0]);
        }
      }
    }

    const buttonDisabled = ref(false);

    async function sendEvent() {
      buttonDisabled.value = true;
      if (!isEdit.value) {
        await postCalendar();
      } else {
        await patchEvent();
      }
      buttonDisabled.value = false;
    }

    function setNewSelect(e) {
      form.trainer.value = e;
    }

    function changeCheck() {
      form.is_group.value = !form.is_group.value;
    }

    return {
      buttonDisabled,
      changeCheck,
      errorMessage,
      form,
      downloadedSelectedChildren,
      sendEvent,
      setNewSelect,
      trainers,
      updateChildrenList,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/colors.scss';
@import '../../assets/css/errors.scss';

@media (min-width: 1248px)
{
  .form {
    height: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    width: 100%;

    .site {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 50%;

      .form-row {
        display: flex !important;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;

        .time-input {
          width: 220px;
        }

        .color-input {
          width: 120px;
        }
      }

      .name-input {
        margin: 20px 12px 20px 20px;
        width: 100%;
      }
    }
    .button {
      background-color: $blue;
      border: 1px solid $blue;
      border-radius: 5px;
      color: white;
      height: 40px;
      margin: 20px;
      width: 300px;
    }
  }
}

@media (max-width: 1247px)
{
  .form {
    height: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    width: 100%;

    .site {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;

      .form-row {
        display: flex !important;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;

        .time-input {
          width: 220px;
        }

        .color-input {
          width: 120px;
        }
      }

      .name-input {
        margin: 20px 12px 20px 20px;
        width: 100%;
      }
    }
    .button {
      background-color: $blue;
      border: 1px solid $blue;
      border-radius: 5px;
      color: white;
      height: 40px;
      margin: 20px;
      width: 300px;
    }
  }
}

</style>
